import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { RestaurantService } from '../../../shared/services/restaurant/restaurant.service';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { ExcelService } from '../../../shared/services/excel/excel.service';
// import { TitleComponent } from '../../../shared/components/title/title.component';
import { RestaurantFilterBarComponent } from '../../../shared/components/restaurant/restaurant-filter-bar/restaurant-filter-bar.component';
import { RestaurantSummaryCardsComponent } from '../../../shared/components/restaurant/restaurant-summary-cards/restaurant-summary-cards.component';
import {
  FilterBarConfig,
  SummaryCard,
} from '../../../shared/interfaces/restaurant-shared.interface';
import { ExportExcelButtonComponent } from '../../../shared/components/export-excel-button/export-excel-button.component';
import { MatIconModule } from '@angular/material/icon';
import {
  PunchesDataResponse,
  PunchDataItem,
  PunchesSummary,
} from '../../../shared/interfaces/restaurant.interface';

@Component({
  selector: 'app-punches',
  standalone: true,
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    CommonModule,
    MatPaginatorModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSlideToggleModule,
    LoaderComponent,
    RestaurantFilterBarComponent,
    RestaurantSummaryCardsComponent,
    ExportExcelButtonComponent,
    MatIconModule,
    RouterLink,
  ],
  templateUrl: './punches.component.html',
  styleUrls: ['./punches.component.css', '../../../shared/styles/layout.css'],
})
export class PunchesComponent implements OnInit, OnDestroy, AfterViewInit {
  isActive = true;

  private subscriptions: Subscription[] = [];
  private updatingChargeIds = new Set<number>();

  dataSource: MatTableDataSource<PunchDataItem> =
    new MatTableDataSource<PunchDataItem>();
  private _liveAnnouncer = inject(LiveAnnouncer);
  myroles: any = {};
  employeeId: string | null = null;
  searchText: string = '';
  selectedFilter: string = 'this_month';
  startDate: Date | null = null;
  endDate: Date | null = null;

  title: string = 'Consumos';
  subtitle: string = 'Consumos del empleado';

  filterBarConfig: FilterBarConfig = {
    hasSearch: false,
    hasBackButton: true,
    backButtonText: 'Volver',
    hasDateRange: true,
    filters: [
      { value: 'last_month', label: 'Mes anterior' },
      { value: 'this_month', label: 'Este mes' },
      { value: 'intervalo', label: 'Intérvalo' },
    ],
    paginatorOptions: [25, 50, 100],
  };

  summaryCards: SummaryCard[] = [];
  punchResponse: PunchesDataResponse | null = null;

  displayedColumns: string[] = [
    'date',
    'punch_type_display',
    'price',
    'price_company',
    'source',
    'status',
    'charge',
  ];

  @ViewChild(MatSort) sort: MatSort = new MatSort();
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(RestaurantFilterBarComponent)
  filterBarComponent!: RestaurantFilterBarComponent;

  constructor(
    private restaurantService: RestaurantService,
    private loaderService: LoaderService,
    private route: ActivatedRoute,
    private location: Location,
    private storageService: StorageService,
    private rolesService: RolesService,
    private excelService: ExcelService<any>
  ) {}

  get isLoading() {
    return this.loaderService.getLoader();
  }

  ngOnInit() {
    this.loaderService.showLoader();
    this.loadRoles();

    this.route.queryParams.subscribe((params) => {
      this.employeeId = params['employeeId'];
      if (this.employeeId) {
        this.subtitle = `Consumos del empleado ${this.employeeId}`;
        this.loadPunchesData();
      }
    });
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    if (this.filterBarComponent) {
      this.paginator = this.filterBarComponent.getPaginator();
      this.dataSource.paginator = this.paginator;
    }
    this.setupFilterPredicate();
  }

  loadRoles() {
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'general',
      this.storageService
    );
    if (permissions) {
      this.myroles = permissions;
    }
  }

  loadPunchesData() {
    if (!this.employeeId) return;

    let startDate: Date;
    let endDate: Date;

    if (this.selectedFilter === 'intervalo' && this.startDate && this.endDate) {
      startDate = this.startDate;
      endDate = this.endDate;
    } else {
      const today = new Date();
      endDate = new Date();
      startDate = new Date();
      switch (this.selectedFilter) {
        case 'this_month':
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          break;
        case 'last_month':
          startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          endDate = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        default:
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          break;
      }
    }

    const body = {
      start_date: this.formatDateForApi(startDate),
      end_date: this.formatDateForApi(endDate),
      external_id: this.employeeId,
    };

    const subscription = this.restaurantService.getPunchesData(body).subscribe({
      next: (response: PunchesDataResponse) => {
        this.punchResponse = response;
        this.dataSource.data = response.data;
        this.updateSummaryCards(response.summary);
        this.loaderService.hideLoader();
      },
      error: (error) => {
        console.error('Error loading punches data:', error);
        this.loaderService.hideLoader();
      },
    });
    this.subscriptions.push(subscription);
  }

  updateSummaryCards(summary: PunchesSummary) {
    this.summaryCards = [
      {
        title: 'Total Consumos',
        value: summary.total_punches.toString(),
        icon: 'restaurant',
      },
      {
        title: 'Consumidos',
        value: summary.eaten.toString(),
        icon: 'check_circle',
      },
      {
        title: 'Pendientes',
        value: summary.pending.toString(),
        icon: 'pending',
      },
      {
        title: 'Desayunos',
        value: summary.breakfast.toString(),
        icon: 'free_breakfast',
      },
      {
        title: 'Comidas',
        value: summary.lunch.toString(),
        icon: 'lunch_dining',
      },
      {
        title: 'Coste',
        value: summary.total_price.toString() + '€',
        icon: 'euro',
      },
      {
        title: 'Coste empresa',
        value: summary.total_price_company.toString() + '€',
        icon: 'euro',
      },
    ];
  }

  formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onFilterChange(filter: string) {
    this.selectedFilter = filter;
    this.loaderService.showLoader();
    this.loadPunchesData();
  }

  onSearchChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchText = input.value;
    this.applyFilters();
  }

  onDateRangeChange(dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  }) {
    this.startDate = dateRange.startDate;
    this.endDate = dateRange.endDate;
    if (this.selectedFilter === 'intervalo' && this.startDate && this.endDate) {
      this.loaderService.showLoader();
      this.loadPunchesData();
    }
  }

  applyFilters() {
    const filterValue = JSON.stringify({
      searchText: this.searchText.toLowerCase(),
    });
    this.dataSource.filter = filterValue;
  }

  setupFilterPredicate() {
    this.dataSource.filterPredicate = (data, filter): boolean => {
      const filterObj = JSON.parse(filter);
      if (filterObj.searchText && filterObj.searchText.trim() !== '') {
        const searchText = filterObj.searchText.toLowerCase();
        return (
          (!!data.employee_external_id &&
            data.employee_external_id.toLowerCase().includes(searchText)) ||
          (!!data.punch_type_display &&
            data.punch_type_display.toLowerCase().includes(searchText))
        );
      }
      return true;
    };
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  onExportClick() {
    this.exportToExcel();
  }

  onBackClick() {
    this.goBack();
  }

  exportToExcel() {
    const allData = this.punchResponse?.data || [];
    const dataToExport = allData.map((item) => ({
      Fecha: new Date(item.date).toLocaleDateString('es-ES'),
      'Tipo de consumo': item.punch_type_display,
      Precio: `${item.price}€`,
      'Coste Empresa': `${item.price_company}€`,
      Estado: item.status === 'eaten' ? 'Consumido' : 'Pendiente',
      Empleado: item.employee_external_id,
      Consumido: item.is_eaten ? 'Sí' : 'No',
      Cobrado: item.charge ? 'Sí' : 'No',
    }));
    const startDateStr = this.punchResponse?.date_range?.start_date || '';
    const endDateStr = this.punchResponse?.date_range?.end_date || '';
    const dateRange =
      startDateStr && endDateStr
        ? `${startDateStr}_${endDateStr}`
        : new Date().toLocaleDateString('es-ES');
    const fileName = `consumos-empleado-${this.employeeId}-${dateRange}`;
    this.excelService.exportExcel(dataToExport, fileName);
  }

  getStatusDisplay(status: string): string {
    switch (status?.toLowerCase()) {
      case 'eaten':
        return 'Consumido';
      case 'pending':
        return 'Pendiente';
      default:
        return status || '';
    }
  }

  getSourceDisplay(item: PunchDataItem): string {
    switch (item.source?.toLowerCase()) {
      case 'sync':
        return 'Consumo';
      case 'booking':
        if (item.booking_punched) return 'Reserva Consumida';
        return 'Reserva';
      default:
        return item.source || '';
    }
  }

  isUpdatingCharge(punch: PunchDataItem): boolean {
    return this.updatingChargeIds.has(punch.id);
  }

  toggleBulkCharge() {
    const punchIds = this.dataSource.data.map((punch) => punch.id);
    const newChargeStatus = this.isActive;

    if (punchIds.length === 0) {
      return;
    }

    // Add all punch IDs to the updating set
    punchIds.forEach((id) => this.updatingChargeIds.add(id));

    const subscription = this.restaurantService
      .bulkUpdatePunchCharge(punchIds, newChargeStatus)
      .subscribe({
        next: (response) => {
          // Update local data with the response
          this.dataSource.data.forEach((punch) => {
            punch.charge = newChargeStatus;
          });
          this.loadPunchesData();
        },
        error: (error) => {
          console.error('Error updating bulk punch charge status:', error);
        },
        complete: () => {
          // Remove all punch IDs from the updating set
          punchIds.forEach((id) => this.updatingChargeIds.delete(id));
        },
      });

    this.subscriptions.push(subscription);
  }

  toggleChargeStatus(punch: PunchDataItem, newChargeStatus: boolean) {
    const punchId = punch.id;
    this.updatingChargeIds.add(punchId);
    const subscription = this.restaurantService
      .updatePunchCharge(punchId, newChargeStatus)
      .subscribe({
        next: () => {
          punch.charge = newChargeStatus;
          this.loadPunchesData();
        },
        error: (error) => {
          console.error('Error updating punch charge status:', error);
        },
        complete: () => {
          this.updatingChargeIds.delete(punchId);
        },
      });
    this.subscriptions.push(subscription);
  }

  getPunchChargeStatus(punch: PunchDataItem): boolean {
    return punch.charge || false;
  }

  goBack() {
    // console.log('Going back to previous page');
    this.location.back();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });
    this.subscriptions = [];
  }
}
